import {LoadProfile, RateCalculator} from '@bellawatt/electric-rate-engine';
import {useInputs} from '@bellawatt/use-inputs';
import {YEAR} from '@generic/data/assumptions';
import {getRates} from '@generic/data/rates';
import calcChargerSetFacilityLoadProfile from '@generic/functions/calcChargerSetFacilityLoadProfile';
import calcScaledFacilityLoadProfile from '@generic/functions/calcScaledFacilityLoadProfile';
import ElectricityBill from '@generic/pages/ChargingCalculator/ElectricityBill';
import RateBreakdownSelector from '@generic/pages/ChargingCalculator/RateBreakdownSelector';
import RateChargePanel from '@generic/pages/ChargingCalculator/RateChargePanel';
import RateComponentCategoryChart from '@generic/pages/ChargingCalculator/RateComponentCategoryChart';
import SupportingCopyPrintout from '@generic/pages/ChargingCalculator/SupportingCopyPrintout';
import PlanningSiteLoadProfile from '@generic/pages/SitePlanner/PlanningSiteLoadProfile';
import PlanningSiteMonthlyDemand from '@generic/pages/SitePlanner/PlanningSiteMonthlyDemand';
import {Box, Grid, makeStyles} from '@material-ui/core';
import React, {useEffect, useMemo, useState} from 'react';
import EVPhaseInRateDisclaimer from '../../components/EVPhaseInRateDisclaimer';
import SmartChargeCommercialBreakdown from '../../components/SmartChargeCommercialBreakdown';

const useStyles = makeStyles(() => ({
  label: {
    fontWeight: 'bold',
    marginRight: '1rem',
    lineHeight: '1.5rem',
  },
  alert: {
    flexGrow: 1,
  },
  flexedGridItem: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function PlanningRateBreakdown({annualCosts}) {
  const inputs = useInputs();
  const classes = useStyles();
  const {separatelyMetered} = inputs;
  RateCalculator.shouldValidate = false;

  const chargerLoadProfileData = calcChargerSetFacilityLoadProfile(inputs);
  const chargerLoadProfile = new LoadProfile(chargerLoadProfileData, {year: YEAR});
  const totalLoadProfile = separatelyMetered
    ? chargerLoadProfile
    : chargerLoadProfile.aggregate(calcScaledFacilityLoadProfile(inputs));

  const maxDemand = totalLoadProfile.max();

  const rates = useMemo(() => {
    const rates = getRates(inputs, maxDemand);
    return rates.reduce(
      (acc, rate) => ({
        ...acc,
        [rate.name]: rate,
      }),
      {}
    );
  }, [inputs, maxDemand]);

  const [activeRateName, setActiveRateName] = useState(Object.keys(rates)[0]);
  const rate = rates[activeRateName];
  const [selectedRateComponent, setSelectedRateComponent] = useState('energy');

  const lowestRateName = annualCosts.sort((a, b) => a.total - b.total)[0].name;
  const selectedAnnualCosts = annualCosts.find((c) => c.name === activeRateName) || annualCosts[0];
  const {siteSccRebate} = selectedAnnualCosts;

  useEffect(() => {
    setActiveRateName(lowestRateName);
  }, [lowestRateName]);

  const rateCalculator =
    rate &&
    new RateCalculator({
      ...rate,
      loadProfile: totalLoadProfile,
    });

  if (!rateCalculator) return null;

  return (
    <Box mt={5}>
      <Box mb={5}>
        <Grid container spacing={2} mb={5}>
          <Grid item xs={12} lg={6}>
            <RateBreakdownSelector
              rateName={rate.name}
              handleChange={setActiveRateName}
              maxDemand={maxDemand}
            />

            <SupportingCopyPrintout rateName={rate.name} />
          </Grid>
          <Grid item xs={12} lg={6} className={classes.flexedGridItem}>
            <SmartChargeCommercialBreakdown />
          </Grid>
        </Grid>
      </Box>

      <Grid container>
        <Grid item xs={12} lg={6}>
          <RateComponentCategoryChart
            rateCalculator={rateCalculator}
            onHover={setSelectedRateComponent}
            sccRebate={siteSccRebate}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box px={10} display="flex" justifyContent="center">
            <RateChargePanel
              rateCalculator={rateCalculator}
              loadProfile={totalLoadProfile}
              rateTitle={rate.title}
              rateComponent={selectedRateComponent}
              sccRebate={siteSccRebate}
            />
          </Box>
        </Grid>
      </Grid>

      <PlanningSiteMonthlyDemand />
      <PlanningSiteLoadProfile />

      <ElectricityBill
        rateCalculator={rateCalculator}
        loadProfile={totalLoadProfile}
        rateTitle={rate.title}
      />

      <Box mb={5}>
        <EVPhaseInRateDisclaimer rateCalculator={rateCalculator} />
      </Box>
    </Box>
  );
}
